import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Binary Finance Page
 * Standalone page with its own design system (Structural Clarity)
 */

// GA Event tracking helper
const trackBookingClick = (label) => {
  if (window.gtag) {
    window.gtag('event', 'booking_click', {
      event_category: 'conversion',
      event_label: label
    });
  }
};

// Diamond SVG component for CTAs
const DiamondIcon = () => (
  <svg viewBox="0 0 14 14" fill="white" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
    <polygon points="7,0 14,7 7,14 0,7"/>
  </svg>
);

const BinaryFinance = () => {
  // Tree state
  const totalGates = 5;
  const [currentGate, setCurrentGate] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [gateStates, setGateStates] = useState(Array(totalGates).fill('hidden'));
  const [showAnchor, setShowAnchor] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const gateRefs = useRef([]);

  // Initialize first gate as active
  useEffect(() => {
    const newStates = [...gateStates];
    newStates[0] = 'active';
    setGateStates(newStates);
  }, []);

  const resolveGate = (gateIndex, answer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    // Mark gate as resolved after delay
    setTimeout(() => {
      const newStates = [...gateStates];
      newStates[gateIndex] = 'resolved';
      
      if (gateIndex < totalGates - 1) {
        // Show next gate after another delay
        setTimeout(() => {
          const updatedStates = [...newStates];
          updatedStates[gateIndex + 1] = 'active';
          setGateStates(updatedStates);
          setCurrentGate(gateIndex + 1);
          
          // Scroll to next gate
          setTimeout(() => {
            gateRefs.current[gateIndex + 1]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 400);
        }, 600);
      } else {
        // All gates complete - show anchor then result
        setTimeout(() => {
          setShowAnchor(true);
          setTimeout(() => {
            setShowResult(true);
          }, 2500);
        }, 700);
      }
      
      setGateStates(newStates);
    }, 1000);
  };

  const resetTree = () => {
    setCurrentGate(0);
    setAnswers([]);
    setShowAnchor(false);
    setShowResult(false);
    const newStates = Array(totalGates).fill('hidden');
    newStates[0] = 'active';
    setGateStates(newStates);
    document.getElementById('treeContainer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Calculate no count for result
  const noCount = answers.filter(a => a === 'no' || a === 'feelings' || a === 'tight').length;

  const getResultContent = () => {
    if (noCount >= 3) {
      return {
        headline: 'The tree surfaced a pattern.',
        body: 'Multiple gates pointed to the same thing: you are making financial decisions without a system. That is not a judgment. It is a structural gap. Binary Finance builds the controls so the next decision is already made before you get to it.'
      };
    } else if (noCount >= 1) {
      return {
        headline: 'You have some structure. It has gaps.',
        body: 'Some of your financial decisions have rules behind them. Others do not. The gaps are where the stress lives. Binary Finance fills those gaps with controls specific enough to follow on your worst day.'
      };
    } else {
      return {
        headline: 'You have controls. The question is whether they are specific enough.',
        body: 'You answered with structure across the board. That puts you ahead. But if those rules are not written down, tested, and organized by zone, they are instinct. Instinct breaks under pressure. Binary Finance formalizes what you already know into a system that holds.'
      };
    }
  };

  const gates = [
    {
      type: 'Gate 1 // Yes or No',
      question: 'Do you know, right now, how much you can safely spend this week without checking your bank account?',
      options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
      responses: {
        yes: 'You have some awareness. That is a start. Let us see if it holds up under pressure.',
        no: 'You are making spending decisions without data. That is not a discipline problem. That is a controls problem.'
      }
    },
    {
      type: 'Gate 2 // Yes or No',
      question: 'If your income dropped by 30% next month, do you already have a written set of rules for what changes?',
      options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
      responses: {
        yes: 'You have controls. They may just need refinement.',
        no: 'You are operating without a system. Every dip is a crisis you solve from scratch.'
      }
    },
    {
      type: 'Gate 3 // This or That',
      question: 'When you make a financial decision under pressure, are you following rules or following feelings?',
      options: [{ label: 'Rules', value: 'rules' }, { label: 'Feelings', value: 'feelings' }],
      responses: {
        rules: 'You have some structure. The question is whether it is specific enough.',
        feelings: 'That is where most people live. No shame in it. It just means you are making every financial decision from scratch, every time.'
      }
    },
    {
      type: 'Gate 4 // Yes or No',
      question: 'Can you name, in one sentence, what changes about your spending when your revenue crosses a specific number?',
      options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
      responses: {
        yes: 'You have some zone awareness. Your controls may just need to be formalized.',
        no: 'You do not have financial zones. Every income level feels the same, which means your spending never adjusts to match reality.'
      }
    },
    {
      type: 'Gate 5 // Somatic',
      question: 'When you think about your money right now, does your body feel open or tight?',
      options: [{ label: 'Open', value: 'open' }, { label: 'Tight', value: 'tight' }],
      responses: {
        open: 'Something is working. The question is what.',
        tight: 'That tightness is not a character flaw. It is the absence of controls. Your body is telling you it does not have enough information to feel safe.'
      }
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@700&family=Work+Sans:wght@400;600&family=Geist+Mono:wght@400;500&display=swap');

        :root {
          --bf-dark: #0D0D0D;
          --bf-light: #FFFFFF;
          --bf-blue: #0057FF;
          --bf-mid: #666666;
          --bf-body-text: #2A2A2A;
          --bf-faded: #CCCCCC;
          --bf-light-gray: #F2F2F2;
          --bf-ghost: #ECECEC;
          --bf-max-width: 780px;
        }

        .bf-page * { margin: 0; padding: 0; box-sizing: border-box; }

        .bf-page {
          font-family: 'Work Sans', sans-serif;
          color: var(--bf-body-text);
          background: var(--bf-light);
          -webkit-font-smoothing: antialiased;
        }

        .bf-section {
          position: relative;
          padding: 100px 40px;
          overflow: hidden;
        }
        .bf-section--dark {
          background: var(--bf-dark);
          color: #F5F5F5;
        }
        .bf-section__inner {
          max-width: var(--bf-max-width);
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .bf-ghost-num {
          position: absolute;
          top: 50px;
          left: -20px;
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 180px;
          line-height: 1;
          color: var(--bf-ghost);
          z-index: 1;
          user-select: none;
          pointer-events: none;
        }
        .bf-section--dark .bf-ghost-num { color: rgba(255,255,255,0.04); }

        .bf-section-label {
          font-family: 'Geist Mono', monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: var(--bf-mid);
          margin-bottom: 16px;
        }
        .bf-section--dark .bf-section-label { color: #888; }

        .bf-statement {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: clamp(32px, 4.5vw, 48px);
          line-height: 1.15;
          color: var(--bf-dark);
          margin-bottom: 36px;
        }
        .bf-statement .bf-faded { color: var(--bf-faded); }
        .bf-section--dark .bf-statement { color: #F5F5F5; }
        .bf-section--dark .bf-statement .bf-faded { color: rgba(255,255,255,0.25); }

        .bf-hero-headline {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: clamp(42px, 6vw, 68px);
          line-height: 1.05;
          color: var(--bf-dark);
          margin-bottom: 32px;
        }

        .bf-superlabel {
          font-family: 'Geist Mono', monospace;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: var(--bf-mid);
          margin-bottom: 28px;
        }

        .bf-prose {
          font-size: 17px;
          line-height: 1.75;
          margin-bottom: 20px;
          max-width: 640px;
        }
        .bf-prose--bold { font-weight: 600; }
        .bf-prose--accent { color: var(--bf-dark); font-weight: 600; }
        .bf-section--dark .bf-prose { color: #CCCCCC; }
        .bf-section--dark .bf-prose--accent { color: #F5F5F5; }

        .bf-prose-break { height: 12px; }

        .bf-stacked-group {
          margin: 24px 0;
          padding-left: 20px;
          border-left: 2px solid var(--bf-light-gray);
        }
        .bf-section--dark .bf-stacked-group { border-left-color: rgba(255,255,255,0.1); }
        .bf-stacked-line {
          font-size: 16px;
          line-height: 1.5;
          padding: 6px 0;
          color: var(--bf-body-text);
        }
        .bf-section--dark .bf-stacked-line { color: #CCCCCC; }

        .bf-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: var(--bf-blue);
          color: white;
          font-family: 'Work Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          padding: 16px 32px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          margin-top: 36px;
          transition: opacity 0.2s;
        }
        .bf-cta:hover { opacity: 0.88; }

        .bf-secondary-cta {
          display: block;
          margin-top: 16px;
          font-family: 'Geist Mono', monospace;
          font-size: 13px;
          color: var(--bf-mid);
          letter-spacing: 0.5px;
          text-decoration: none;
        }
        .bf-section--dark .bf-secondary-cta { color: #888; }

        .bf-gate-cards {
          display: flex;
          flex-direction: column;
          gap: 32px;
          margin: 40px 0;
        }
        .bf-gate-card {
          border-left: 3px solid var(--bf-blue);
          padding: 32px 28px;
          background: var(--bf-light-gray);
        }
        .bf-gate-card--featured { background: #E8EEFF; }
        .bf-gate-card__name {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 20px;
          color: var(--bf-dark);
          margin-bottom: 8px;
        }
        .bf-gate-card__desc {
          font-size: 15px;
          color: var(--bf-mid);
          margin-bottom: 16px;
        }
        .bf-gate-card .bf-stacked-group { border-left: none; padding-left: 0; margin: 12px 0; }
        .bf-gate-card .bf-stacked-line { font-size: 15px; color: var(--bf-body-text); padding: 4px 0; }
        .bf-gate-card__price {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 24px;
          color: var(--bf-dark);
          margin-top: 20px;
        }
        .bf-gate-card__price span {
          font-family: 'Geist Mono', monospace;
          font-size: 12px;
          font-weight: 500;
          color: var(--bf-mid);
          letter-spacing: 1px;
          margin-left: 12px;
          text-transform: uppercase;
        }

        .bf-disclaimer {
          font-size: 12px;
          color: #888;
          margin-top: 48px;
          line-height: 1.5;
          max-width: 480px;
        }
        .bf-section--dark .bf-disclaimer { color: #555; }

        .bf-section-divider {
          height: 1px;
          background: var(--bf-light-gray);
          max-width: var(--bf-max-width);
          margin: 0 auto;
        }

        /* MINI DECISION TREE */
        .bf-tree-container { margin-top: 32px; }

        .bf-tree-tracker {
          display: flex;
          align-items: center;
          gap: 0;
          margin-bottom: 40px;
        }
        .bf-tree-tracker__dot {
          width: 8px;
          height: 8px;
          border-radius: 0;
          background: var(--bf-light-gray);
          flex-shrink: 0;
          transition: background 0.3s;
        }
        .bf-tree-tracker__dot--active { background: var(--bf-blue); }
        .bf-tree-tracker__dot--resolved { background: var(--bf-dark); }
        .bf-tree-tracker__line {
          height: 2px;
          width: 32px;
          background: var(--bf-light-gray);
          flex-shrink: 0;
          transition: background 0.3s;
        }
        .bf-tree-tracker__line--resolved { background: var(--bf-dark); }
        .bf-tree-tracker__label {
          font-family: 'Geist Mono', monospace;
          font-size: 9px;
          letter-spacing: 1px;
          color: var(--bf-mid);
          margin-left: 12px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .bf-tree-gate {
          border-left: 3px solid var(--bf-light-gray);
          padding: 28px 24px;
          background: white;
          margin-bottom: 16px;
          transition: all 0.4s ease, max-height 0.5s ease, padding 0.5s ease, margin 0.5s ease, opacity 0.4s ease;
          opacity: 0;
          max-height: 0;
          padding: 0 24px;
          margin-bottom: 0;
          overflow: hidden;
          pointer-events: none;
        }
        .bf-tree-gate--active {
          border-left-color: var(--bf-blue);
          background: white;
          opacity: 1;
          max-height: 500px;
          padding: 28px 24px;
          margin-bottom: 16px;
          pointer-events: auto;
        }
        .bf-tree-gate--resolved {
          border-left-color: var(--bf-dark);
          background: var(--bf-light-gray);
          opacity: 0.65;
          max-height: 500px;
          padding: 28px 24px;
          margin-bottom: 16px;
          pointer-events: none;
        }

        .bf-tree-gate__type {
          font-family: 'Geist Mono', monospace;
          font-size: 10px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--bf-mid);
          margin-bottom: 10px;
        }
        .bf-tree-gate__question {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 20px;
          line-height: 1.3;
          color: var(--bf-dark);
          margin-bottom: 16px;
        }
        .bf-tree-gate__buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .bf-tree-gate__btn {
          font-family: 'Work Sans', sans-serif;
          font-size: 14px;
          font-weight: 600;
          padding: 12px 24px;
          border: 2px solid var(--bf-dark);
          background: transparent;
          color: var(--bf-dark);
          cursor: pointer;
          transition: all 0.15s;
          min-width: 100px;
          text-align: center;
        }
        .bf-tree-gate__btn:hover {
          background: var(--bf-dark);
          color: white;
        }
        .bf-tree-gate__btn--selected {
          background: var(--bf-dark);
          color: white;
          pointer-events: none;
        }
        .bf-tree-gate__btn--unselected {
          opacity: 0.3;
          pointer-events: none;
        }

        .bf-tree-gate__response {
          margin-top: 14px;
          font-size: 15px;
          line-height: 1.6;
          color: var(--bf-mid);
          display: none;
        }
        .bf-tree-gate__response--visible { display: block; }

        .bf-tree-gate__answer {
          font-family: 'Geist Mono', monospace;
          font-size: 10px;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: var(--bf-blue);
          margin-top: 8px;
          display: none;
        }
        .bf-tree-gate--resolved .bf-tree-gate__answer { display: block; }

        .bf-tree-anchor {
          border-left: 3px solid var(--bf-blue);
          padding: 0 24px;
          background: white;
          margin-top: 0;
          opacity: 0;
          max-height: 0;
          overflow: hidden;
          transition: all 0.5s ease;
          pointer-events: none;
        }
        .bf-tree-anchor--visible {
          opacity: 1;
          max-height: 300px;
          padding: 28px 24px;
          margin-top: 24px;
          pointer-events: auto;
        }
        .bf-tree-anchor__label {
          font-family: 'Geist Mono', monospace;
          font-size: 10px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--bf-blue);
          margin-bottom: 12px;
        }
        .bf-tree-anchor__question {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 22px;
          line-height: 1.3;
          color: var(--bf-dark);
          margin-bottom: 16px;
        }
        .bf-tree-anchor__body {
          font-size: 15px;
          line-height: 1.6;
          color: var(--bf-mid);
        }

        .bf-tree-result {
          margin-top: 0;
          padding: 0 28px;
          border-left: 3px solid var(--bf-blue);
          background: #E8EEFF;
          opacity: 0;
          max-height: 0;
          overflow: hidden;
          transition: all 0.5s ease;
        }
        .bf-tree-result--visible {
          opacity: 1;
          max-height: 500px;
          padding: 32px 28px;
          margin-top: 32px;
        }
        .bf-tree-result__headline {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 20px;
          color: var(--bf-dark);
          margin-bottom: 12px;
        }
        .bf-tree-result__body {
          font-size: 15px;
          line-height: 1.6;
          color: var(--bf-body-text);
          margin-bottom: 20px;
        }

        .bf-tree-reset {
          font-family: 'Geist Mono', monospace;
          font-size: 11px;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: var(--bf-mid);
          background: none;
          border: none;
          cursor: pointer;
          margin-top: 24px;
          padding: 0;
          transition: color 0.2s;
        }
        .bf-tree-reset:hover { color: var(--bf-dark); }

        @media (max-width: 768px) {
          .bf-section { padding: 72px 24px; }
          .bf-ghost-num { font-size: 120px; top: 40px; left: -10px; }
          .bf-hero-headline { font-size: 36px; }
          .bf-statement { font-size: 28px; }
          .bf-gate-card { padding: 24px 20px; }
          .bf-tree-gate__question { font-size: 18px; }
          .bf-tree-tracker__line { width: 20px; }
        }
      `}</style>

      <div className="bf-page">
        {/* Site Header */}
        <Header />
        
        {/* 01 / HERO */}
        <section className="bf-section">
          <div className="bf-ghost-num">01</div>
          <div className="bf-section__inner">
            <div className="bf-superlabel">Binary Game Presents</div>
            <h1 className="bf-hero-headline">You Make Money.<br/>Now Feel in Control of it.</h1>
            <p className="bf-prose">Binary Finance builds clear rules for your spending and your revenue so you stop making the same financial decisions over and over again.</p>
            <a href="#pricing" className="bf-cta" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>
              <DiamondIcon />
              Book a Binary Finance session
            </a>
          </div>
        </section>

        {/* 02 / THE PROBLEM */}
        <section className="bf-section">
          <div className="bf-ghost-num">02</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">The Problem</div>
            <h2 className="bf-statement">You make money. <span className="bf-faded">That part works.</span></h2>
            <p className="bf-prose">The part that does not work is what happens after it hits your account.</p>
            <p className="bf-prose">You spend without a real system. You save when you remember to. You check your balance and hope the number makes sense. When it does not, you adjust on the fly and tell yourself you will figure it out next month.</p>
            <p className="bf-prose bf-prose--accent">Next month never comes.</p>
            <p className="bf-prose">This is not a discipline problem. You are not bad with money. You just never had anyone build you the controls.</p>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 03 / WHAT IT IS */}
        <section className="bf-section">
          <div className="bf-ghost-num">03</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">What Binary Finance Is</div>
            <h2 className="bf-statement">Your numbers, <span className="bf-faded">turned into rules.</span></h2>
            <p className="bf-prose">Binary Finance is a financial controls system built on the Binary Game method.</p>
            <p className="bf-prose">It takes your actual numbers and turns them into rules you can follow without remaking the same decision every time.</p>

            <p className="bf-prose bf-prose--bold">Revenue rules:</p>
            <div className="bf-stacked-group">
              <div className="bf-stacked-line">Tell you what to do at every income level</div>
              <div className="bf-stacked-line">Each zone has its own instructions, already defined in advance</div>
              <div className="bf-stacked-line">When you cross into the next zone, the rules change with it</div>
            </div>

            <p className="bf-prose bf-prose--bold">Expense rules:</p>
            <div className="bf-stacked-group">
              <div className="bf-stacked-line">Every dollar has a job before you spend it</div>
              <div className="bf-stacked-line">Not a budget you hope to stick to</div>
              <div className="bf-stacked-line">A set of controls that gives you a clear yes or no before the moment of decision</div>
            </div>

            <div className="bf-prose-break"></div>
            <p className="bf-prose">Most financial support is built around maintenance: bookkeeping, accounting, reporting, and ongoing management.</p>
            <p className="bf-prose bf-prose--accent">Binary Finance is built to install the rules once so you can use them over and over again.</p>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 04 / MINI DECISION TREE */}
        <section className="bf-section" style={{ background: '#FAFAFA' }}>
          <div className="bf-ghost-num">04</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">Do I Need Financial Controls?</div>
            <h2 className="bf-statement">Most people do not realize <span className="bf-faded">until they feel it in their body.</span></h2>
            <p className="bf-prose">Try a small version of the Binary Game right now. Five gates. Answer honestly.</p>

            <div className="bf-tree-container" id="treeContainer">
              {/* Tracker */}
              <div className="bf-tree-tracker">
                {gates.map((_, i) => (
                  <React.Fragment key={i}>
                    <div 
                      className={`bf-tree-tracker__dot ${
                        gateStates[i] === 'active' ? 'bf-tree-tracker__dot--active' : 
                        gateStates[i] === 'resolved' ? 'bf-tree-tracker__dot--resolved' : ''
                      }`}
                    />
                    {i < gates.length - 1 && (
                      <div className={`bf-tree-tracker__line ${gateStates[i] === 'resolved' ? 'bf-tree-tracker__line--resolved' : ''}`} />
                    )}
                  </React.Fragment>
                ))}
                <div className="bf-tree-tracker__label">
                  {showResult ? 'Complete' : `Gate ${currentGate + 1} of 5`}
                </div>
              </div>

              {/* Gates */}
              {gates.map((gate, i) => (
                <div 
                  key={i}
                  ref={el => gateRefs.current[i] = el}
                  className={`bf-tree-gate ${
                    gateStates[i] === 'active' ? 'bf-tree-gate--active' : 
                    gateStates[i] === 'resolved' ? 'bf-tree-gate--resolved' : ''
                  }`}
                >
                  <div className="bf-tree-gate__type">{gate.type}</div>
                  <div className="bf-tree-gate__question">{gate.question}</div>
                  <div className="bf-tree-gate__buttons">
                    {gate.options.map((opt) => (
                      <button
                        key={opt.value}
                        className={`bf-tree-gate__btn ${
                          answers[i] === opt.value ? 'bf-tree-gate__btn--selected' : 
                          answers[i] && answers[i] !== opt.value ? 'bf-tree-gate__btn--unselected' : ''
                        }`}
                        onClick={() => resolveGate(i, opt.value)}
                        disabled={gateStates[i] !== 'active'}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {answers[i] && (
                    <div className="bf-tree-gate__response bf-tree-gate__response--visible">
                      {gate.responses[answers[i]]}
                    </div>
                  )}
                  <div className="bf-tree-gate__answer">{answers[i]?.toUpperCase()}</div>
                </div>
              ))}

              {/* Anchor */}
              <div className={`bf-tree-anchor ${showAnchor ? 'bf-tree-anchor--visible' : ''}`}>
                <div className="bf-tree-anchor__label">Anchor Question</div>
                <div className="bf-tree-anchor__question">"Am I managing my money, or am I just reacting to it?"</div>
                <div className="bf-tree-anchor__body">If you answered honestly, the tree just showed you.</div>
              </div>

              {/* Result */}
              <div className={`bf-tree-result ${showResult ? 'bf-tree-result--visible' : ''}`}>
                <div className="bf-tree-result__headline">{getResultContent().headline}</div>
                <div className="bf-tree-result__body">{getResultContent().body}</div>
                <a href="#pricing" className="bf-cta" style={{ marginTop: '16px' }} onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <DiamondIcon />
                  Book a Binary Finance session
                </a>
                <a 
                  href="https://app.usemotion.com/meet/marco-hansell/binaryconsult" 
                  className="bf-secondary-cta"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Set up a free consultation
                </a>
              </div>

              {showResult && (
                <button className="bf-tree-reset" onClick={resetTree}>Run the tree again</button>
              )}
            </div>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 05 / WHO IT'S FOR */}
        <section className="bf-section">
          <div className="bf-ghost-num">05</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">Who This Is For</div>
            <h2 className="bf-statement">You make money <span className="bf-faded">but do not feel in control of it.</span></h2>

            <p className="bf-prose bf-prose--bold">Built for:</p>
            <div className="bf-stacked-group">
              <div className="bf-stacked-line">Individuals who want clarity over personal spending</div>
              <div className="bf-stacked-line">Creators</div>
              <div className="bf-stacked-line">Freelancers and self-employed people</div>
              <div className="bf-stacked-line">Small businesses managing under $500,000 annually</div>
            </div>

            <p className="bf-prose">If the sentence <strong>"I make money but I do not know where it goes"</strong> has ever come out of your mouth, this is for you.</p>
            <p className="bf-prose">If your finances are significantly more complex than that, reach out directly for a higher-touch version.</p>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 06 / WHAT YOU GET */}
        <section className="bf-section">
          <div className="bf-ghost-num">06</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">What You Get</div>
            <h2 className="bf-statement">You leave with a system, <span className="bf-faded">not a feeling.</span></h2>
            <p className="bf-prose">The build begins the moment the numbers are in view.</p>
            <p className="bf-prose bf-prose--accent">The session is where the rules get built and locked in.</p>

            <p className="bf-prose bf-prose--bold">You leave with:</p>
            <div className="bf-stacked-group">
              <div className="bf-stacked-line">A dashboard containing the decision trees for your money logic</div>
              <div className="bf-stacked-line">An exportable summary document you can reference anywhere</div>
              <div className="bf-stacked-line">A system that reduces guesswork when money gets emotional, unclear, or tight</div>
            </div>

            <p className="bf-prose">This is not a budget. This is not a plan. This is a controls system designed to keep your decisions consistent with reality.</p>
            <a href="#pricing" className="bf-cta" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>
              <DiamondIcon />
              Book a Binary Finance session
            </a>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 07 / WHY THIS EXISTS */}
        <section className="bf-section">
          <div className="bf-ghost-num">07</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">Why This Exists</div>
            <h2 className="bf-statement">Financial support <span className="bf-faded">is not the same as financial control.</span></h2>
            <p className="bf-prose">I built Binary Finance because I got tired of repeating the same financial pattern at completely different scales.</p>
            <p className="bf-prose">I lost $35 million not because I did not have support, but because I learned the hard way that having financial support is not the same as having real financial control.</p>
            <p className="bf-prose">I had people. I had budgets. I had reporting.</p>
            <p className="bf-prose bf-prose--accent">What I did not have were controls this specific.</p>
            <p className="bf-prose">I did not have rules that changed when revenue dropped, rules that changed when money came in, rules that told me what to spend, when, and under what conditions.</p>
            <p className="bf-prose">Binary Finance is the system I built to stop repeating that pattern.</p>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 08 / THE GAP */}
        <section className="bf-section">
          <div className="bf-ghost-num">08</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">The Gap</div>
            <h2 className="bf-statement">Below $500k, <span className="bf-faded">what is usually missing is a controls system.</span></h2>
            <p className="bf-prose">If you make under $500,000 a year, your financial process probably looks like some combination of generic budgeting advice, bookkeeping, tax help, and investment or stock advice.</p>

            <p className="bf-prose bf-prose--bold">What is usually missing is something that tells you:</p>
            <div className="bf-stacked-group">
              <div className="bf-stacked-line">What to do at your current income level</div>
              <div className="bf-stacked-line">What changes when money gets tight</div>
              <div className="bf-stacked-line">What counts as safe to spend</div>
              <div className="bf-stacked-line">What needs to wait</div>
              <div className="bf-stacked-line">How revenue and expenses actually interact in real life</div>
            </div>

            <p className="bf-prose bf-prose--accent">Binary Finance fills that gap. Not with advice. Not with a course. With a financial controls system built to your actual numbers in a live session.</p>
          </div>
        </section>

        <div className="bf-section-divider"></div>

        {/* 09 / PRICING */}
        <section className="bf-section" id="pricing">
          <div className="bf-ghost-num">09</div>
          <div className="bf-section__inner">
            <div className="bf-section-label">Pricing</div>
            <h2 className="bf-statement">Pick what you need. <span className="bf-faded">Book it.</span></h2>
            <div className="bf-gate-cards">
              <div className="bf-gate-card">
                <div className="bf-gate-card__name">Binary Finance: Expenses</div>
                <div className="bf-gate-card__desc">90-minute session. Your spending turned into rules.</div>
                <div className="bf-stacked-group">
                  <div className="bf-stacked-line">What you can safely spend</div>
                  <div className="bf-stacked-line">What changes when money gets tight</div>
                  <div className="bf-stacked-line">How to stop leaking money</div>
                  <div className="bf-stacked-line">How to make expense decisions without guessing</div>
                </div>
                <div className="bf-gate-card__price">$1,000 <span>Launch Price</span></div>
                <a 
                  href="https://book.stripe.com/dRm5kEgxJcKL3ga8QucMM0q" 
                  className="bf-cta" 
                  style={{ marginTop: '20px', fontSize: '13px', padding: '12px 24px' }}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackBookingClick('Binary_Finance_Expenses_1000')}
                >
                  <DiamondIcon />
                  Book Binary Finance: Expenses
                </a>
              </div>
              <div className="bf-gate-card">
                <div className="bf-gate-card__name">Binary Finance: Revenue</div>
                <div className="bf-gate-card__desc">90-minute session. Your income turned into rules.</div>
                <div className="bf-stacked-group">
                  <div className="bf-stacked-line">What numbers actually matter</div>
                  <div className="bf-stacked-line">What you need to make by the day, week, and month</div>
                  <div className="bf-stacked-line">What changes when revenue is strong or weak</div>
                  <div className="bf-stacked-line">How to stop feeling vague and reactive about income</div>
                </div>
                <div className="bf-gate-card__price">$1,000 <span>Launch Price</span></div>
                <a 
                  href="https://book.stripe.com/fZu6oIgxJfWXcQK7MqcMM0r" 
                  className="bf-cta" 
                  style={{ marginTop: '20px', fontSize: '13px', padding: '12px 24px' }}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackBookingClick('Binary_Finance_Revenue_1000')}
                >
                  <DiamondIcon />
                  Book Binary Finance: Revenue
                </a>
              </div>
              <div className="bf-gate-card bf-gate-card--featured">
                <div className="bf-gate-card__name">Binary Finance: Complete</div>
                <div className="bf-gate-card__desc">Two 90-minute sessions. Revenue and expenses built together under one system.</div>
                <div className="bf-stacked-group">
                  <div className="bf-stacked-line">Full spending rules</div>
                  <div className="bf-stacked-line">Full revenue rules</div>
                  <div className="bf-stacked-line">Both systems working together</div>
                  <div className="bf-stacked-line">One set of controls for your entire financial picture</div>
                </div>
                <div className="bf-gate-card__price">$1,500 <span>Launch Price</span></div>
                <a 
                  href="https://book.stripe.com/00w8wQ3KXbGHaIC0jYcMM0s" 
                  className="bf-cta" 
                  style={{ marginTop: '20px', fontSize: '13px', padding: '12px 24px' }}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackBookingClick('Binary_Finance_Complete_1500')}
                >
                  <DiamondIcon />
                  Book Binary Finance: Complete
                </a>
              </div>
            </div>
            <p className="bf-prose bf-prose--accent">No application required. Just book it.</p>
            <a 
              href="https://app.usemotion.com/meet/marco-hansell/binaryconsult" 
              className="bf-secondary-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              Set up a free consultation
            </a>
          </div>
        </section>

        {/* 10 / CLOSING */}
        <section className="bf-section" style={{ padding: '48px 40px' }}>
          <div className="bf-section__inner">
            <div className="bf-disclaimer" style={{ color: '#999', marginTop: 0 }}>
              Binary Finance is a financial decision-support and systems-building service. It is not investment, legal, or tax advice.
            </div>
          </div>
        </section>
        
        {/* Site Footer */}
        <Footer />
      </div>
    </>
  );
};

export default BinaryFinance;
